import { CallingService } from '../calling.service';
import { OzonetelSDKCoreService } from './ozonetel-sdk-core.service';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Ozonetel dialer API facade. When sdkCore is initialized (ozonetel_dialer_mode: 'sdk'),
 * mode/campaign/manual-call operations route through OzonetelSDKCoreService so a single
 * DigitalConsole instance owns the agent session. Hold/mute/hangup still use server REST.
 */
export class OzonetelController {
    constructor(
        private callingService: CallingService,
        private sdkCore?: OzonetelSDKCoreService
    ) {}

    pauseCall(params): Observable<any> {
        return this.callingService.pauseOzonetelCall(params);
    }

    resumeCall(): Observable<any> {
        return this.callingService.resumeOzonetelCall();
    }

    stopCall(): Observable<any> {
        return this.callingService.stopOzonetelCall();
    }

    holdCall(): Observable<any> {
        return this.callingService.holdOzonetelCall();
    }

    unholdCall(): Observable<any> {
        return this.callingService.unholdOzonetelCall();
    }

    muteCall(): Observable<any> {
        return this.callingService.muteOzonetelCall();
    }

    unmuteCall(): Observable<any> {
        return this.callingService.unmuteOzonetelCall();
    }

    hangupCall(): Observable<any> {
        return this.callingService.hangupOzonetelCall();
    }

    sendDTMF(digit: string): Observable<any> {
        return this.callingService.sendOzonetelDTMF(digit);
    }

    toggleStartMode(): Observable<any> {
        if (this.sdkCore && this.sdkCore.isAgentInitialized()) {
            return from(this.sdkCore.setAgentMode('MANUAL', 'IDLE')).pipe(
                map((result) => ({
                    resultShort: 'success',
                    resultLong: 'Agent mode set to MANUAL',
                    data: result
                })),
                catchError((error) => of({
                    resultShort: 'failure',
                    resultLong: error.message || 'Failed to set manual mode',
                    error
                }))
            );
        }
        return this.callingService.startOzonetelManualMode();
    }

    toggleStopMode(): Observable<any> {
        if (this.sdkCore && this.sdkCore.isAgentInitialized()) {
            return from(this.sdkCore.setAgentInboundReady()).pipe(
                map((result) => ({
                    resultShort: 'success',
                    resultLong: 'Agent mode set to INBOUND',
                    data: result
                })),
                catchError((error) => of({
                    resultShort: 'failure',
                    resultLong: error.message || 'Failed to set auto mode',
                    error
                }))
            );
        }
        return this.callingService.stopOzonetelManualMode();
    }

    mergeCall(postObj): Observable<any> {
        return this.callingService.mergeOzonetelCall(postObj);
    }

    unMergeCall(): Observable<any> {
        return this.callingService.unMergeOzonetelCall();
    }

    transferCall(postObj): Observable<any> {
        return this.callingService.transferOzonetelCall(postObj);
    }

    conferenceCall(postObj): Observable<any> {
        return this.callingService.conferenceOzonetelCall(postObj);
    }

    autoDisposeCall(postObj): Observable<any> {
        return this.callingService.autoDisposeOzonetelCall(postObj);
    }

    getCampaigns(): Observable<any> {
        if (this.sdkCore && this.sdkCore.isAgentInitialized()) {
            return from(this.sdkCore.getCampaigns()).pipe(
                map((result) => ({
                    resultShort: 'success',
                    data: result
                })),
                catchError((error) => of({
                    resultShort: 'failure',
                    resultLong: error.message || 'Failed to get campaigns',
                    error
                }))
            );
        }
        return this.callingService.getOzonetelCampaigns();
    }

    initManualCall(campId: string, phoneNumber: string, uui?: string): Observable<any> {
        if (this.sdkCore && this.sdkCore.isAgentInitialized()) {
            return from(this.sdkCore.initManualCall(campId, phoneNumber, uui)).pipe(
                map((result) => ({
                    resultShort: 'success',
                    resultLong: 'Manual call initiated',
                    data: result
                })),
                catchError((error) => of({
                    resultShort: 'failure',
                    resultLong: error.message || 'Failed to initiate manual call',
                    error
                }))
            );
        }
        return this.callingService.initOzonetelManualCall({ campId, phoneNumber, uui });
    }
}
